import { ChevronDown, ChevronUp } from 'lucide-react';

export default function DataTable({
  columns,
  data,
  isLoading,
  sortBy,
  sortDir,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  stickyColumnKey,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function handleHeaderClick(column) {
    if (!column.sortable) return;
    if (sortBy === column.key) {
      onSortChange(column.key, sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column.key, 'desc');
    }
  }

  function getCellWrapClass(wrap) {
    if (wrap === 'wrap') return 'whitespace-normal break-words';
    if (wrap === 'hidden') return 'overflow-hidden whitespace-nowrap';
    return 'overflow-hidden text-ellipsis whitespace-nowrap';
  }

  function getColumnStyle(column) {
    const style = {};
    if (column.maxWidth) {
      const maxWidth = typeof column.maxWidth === 'number' ? `${column.maxWidth}px` : column.maxWidth;
      style.maxWidth = maxWidth;
      style.width = maxWidth;
    }
    if (stickyColumnKey && column.key === stickyColumnKey) {
      style.position = 'sticky';
      style.left = 0;
    }
    return Object.keys(style).length ? style : undefined;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-cyan-100 bg-white">
      
      
      <div className="flex items-center justify-between border-t border-cyan-100 px-3 py-2 text-sm text-slate-600">
        <span>Tổng {total.toLocaleString('vi-VN')} bản ghi</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40"
          >
            Trước
          </button>
          <span className="text-xs">
            Trang {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40"
          >
            Sau
          </button>
        </div>
      </div>

      <div className="max-h-[60vh] overflow-auto">
        <table className="min-w-full table-fixed border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-[0.14em] text-black">
              <th className="border-b border-slate-200 px-3 py-2 font-semibold">#</th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => handleHeaderClick(column)}
                  style={getColumnStyle(column)}
                  className={`border-b border-slate-200 px-3 py-2 font-semibold ${
                    column.sortable ? 'cursor-pointer select-none hover:text-slate-700' : ''
                  } ${column.maxWidth ? getCellWrapClass(column.wrap) : ''} ${
                    stickyColumnKey === column.key ? 'z-20 bg-slate-50' : ''
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.label}
                    {column.sortable && sortBy === column.key && (
                      sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : data.length ? (
              data.map((row, index) => {
                const rowBgClass = index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';
                return (
                  <tr key={row.id ?? index} className={`align-top ${rowBgClass}`}>
                    <td className="border-b border-slate-100 px-3 py-2 text-sm text-slate-500">
                      {(page - 1) * pageSize + index + 1}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        style={getColumnStyle(column)}
                        title={column.wrap === 'truncate' || (!column.wrap && column.maxWidth) ? String(row[column.key] ?? '') : undefined}
                        className={`border-b border-slate-100 px-3 py-2 text-sm text-slate-700 ${
                          column.maxWidth ? getCellWrapClass(column.wrap) : ''
                        } ${
                          stickyColumnKey === column.key ? `z-10 ${rowBgClass}` : ''
                        }`}
                      >
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-sm text-slate-500">
                  Không có dữ liệu.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
